import { SlidingWindowGuard } from './sliding-window-guard';

export type ClientProtectionBlockReason =
    | 'automated-interaction'
    | 'interaction-rate'
    | 'unsafe-paste'
    | 'untrusted-submit';

export type ClientProtectionBlockHandler = (reason: ClientProtectionBlockReason) => void;

const DANGEROUS_SCRIPT_PATTERN = /<\s*\/?\s*(?:script|iframe|object|embed|base|meta|link)\b|\bon[a-z0-9_-]+\s*=|\b(?:java|vb)\s*script\s*:|\bdata\s*:\s*text\s*\/\s*html\b/i;

const decodeObfuscatedSeparators = (value: string): string => value
    .replace(/&#x([\da-f]+);?/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&#(\d+);?/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&(colon|tab|newline);?/gi, (_, entity: string) => {
        const separators: Record<string, string> = {
            colon: ':',
            tab: '\t',
            newline: '\n',
        };

        return separators[entity.toLowerCase()] ?? '';
    });

/** Identifica apenas conteúdo que pode virar marcação/URL executável, não texto comum. */
export const containsDangerousScriptPayload = (value: string): boolean => {
    if (!value) return false;

    const decoded = decodeObfuscatedSeparators(value);
    const compact = decoded.replace(/[\s\u0000-\u001f\u007f\u200b-\u200d\ufeff]+/g, '');

    return DANGEROUS_SCRIPT_PATTERN.test(decoded) ||
        /(?:javascript|vbscript):|data:text\/html/i.test(compact);
};

const elementFromTarget = (target: EventTarget | null): Element | null => {
    return target instanceof Element ? target : null;
};

const isEditableTarget = (target: EventTarget | null): boolean => {
    const element = elementFromTarget(target);
    if (!element) return false;

    const editable = element.closest('input, textarea, [contenteditable]');
    return !!editable && editable.getAttribute('contenteditable') !== 'false';
};

const isContentEditableTarget = (target: EventTarget | null): boolean => {
    const element = elementFromTarget(target);
    const editable = element?.closest('[contenteditable]');
    return !!editable && editable.getAttribute('contenteditable') !== 'false';
};

const isCoarsePointer = (): boolean => window.matchMedia?.('(pointer: coarse)').matches ?? false;

const stopEvent = (event: Event): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
};

/**
 * Proteções de interface aplicadas uma única vez ao documento inteiro.
 * Elas reduzem automações ingênuas e evitam colar HTML executável em editores,
 * mas não substituem autorização, validação e rate limiting no servidor.
 */
export const installClientProtection = (onBlocked?: ClientProtectionBlockHandler): (() => void) => {
    const interactionGuard = new SlidingWindowGuard();

    const block = (event: Event, reason: ClientProtectionBlockReason) => {
        stopEvent(event);
        onBlocked?.(reason);
    };

    const onPointerDown = (event: PointerEvent) => {
        if (!event.isTrusted) {
            block(event, 'automated-interaction');
            return;
        }

        const decision = interactionGuard.check();
        if (!decision.allowed) block(event, 'interaction-rate');
    };

    const onSubmit = (event: SubmitEvent) => {
        if (!event.isTrusted) block(event, 'untrusted-submit');
    };

    const onPaste = (event: ClipboardEvent) => {
        if (!isEditableTarget(event.target)) return;

        const plainText = event.clipboardData?.getData('text/plain') ?? '';
        const html = event.clipboardData?.getData('text/html') ?? '';

        if (
            containsDangerousScriptPayload(plainText) ||
            (isContentEditableTarget(event.target) && containsDangerousScriptPayload(html))
        ) {
            block(event, 'unsafe-paste');
        }
    };

    const preventOutsideTextFields = (event: Event) => {
        if (isCoarsePointer() && !isEditableTarget(event.target)) event.preventDefault();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('paste', onPaste, true);

    // Mesmo comportamento de proteção de texto usado no Paçoca Clicker, agora
    // aplicado à interface inteira sem impedir edição de campos de formulário.
    for (const eventName of ['copy', 'cut', 'contextmenu', 'selectstart']) {
        document.addEventListener(eventName, preventOutsideTextFields);
    }

    return () => {
        document.removeEventListener('pointerdown', onPointerDown, true);
        document.removeEventListener('submit', onSubmit, true);
        document.removeEventListener('paste', onPaste, true);

        for (const eventName of ['copy', 'cut', 'contextmenu', 'selectstart']) {
            document.removeEventListener(eventName, preventOutsideTextFields);
        }
    };
};
